import {
  EnterpriseProxy,
  HomePageProxy,
  UpdateUserProfileCommand,
  UserEnterpriseInfoViewModel,
  UserEnterpriseInfoViewModelListResponseResult,
  UserProxy,
} from '@/app/proxy/proxies';
import { SharedModule } from '@/app/shared/shared.module';
import { Component, OnInit, ViewChild } from '@angular/core';
import { finalize } from 'rxjs';
import { LocalStorageService } from '@/app/core/storage/local-storage.service';
import { TutorialService } from '@/app/core/services/tutorial.service';
import { RequestCallbackComponent } from '../account/request-callback/request-callback.component';
import { DkSelectPlantationComponent } from './dk-select-plantation/dk-select-plantation.component';
import { Router } from '@angular/router';
import { MapWidgetComponent } from './map-widget/map-widget.component';
import { PlantationService } from '@/app/core/services/plantation.service';
import { HomeHeaderComponent } from './home-header/home-header.component';
import { HomeWarningsAndAlertsComponent } from './home-warnings-and-alerts/home-warnings-and-alerts.component';
import { HomeProblematicSpotsComponent } from './home-problematic-spots/home-problematic-spots.component';
import { HomeUpcomingIrrigationsComponent } from './home-upcoming-irrigations/home-upcoming-irrigations.component';
import {
  PremiumFeatureKey,
  PurchasePremiumComponent,
} from '../shared/components/purchase-premium/purchase-premium.component';
import { TranslateService } from '@ngx-translate/core';
import { DialogService } from 'primeng/dynamicdialog';
import { WidgetInfoBoxComponent } from './home-warnings-and-alerts/widget-info-box/widget-info-box.component';
import { HomeFilizIrrigationInfoComponent } from './home-filiz-irrigation-info/home-filiz-irrigation-info.component';
import { MixpanelTrackerService } from '@/app/core/analytics/mixpanel-tracker.service';
import { HomeMapWidgetService } from './map-widget/home-map-widget.service';
import { SupportChatBubbleComponent } from '../support-ticket/bubble/support-chat-bubble.component';
import { UserService } from '@/app/core/services/user.service';

@Component({
  selector: 'app-home',
  imports: [
    SharedModule,
    HomeHeaderComponent,
    HomeWarningsAndAlertsComponent,
    HomeProblematicSpotsComponent,
    HomeFilizIrrigationInfoComponent,
    HomeUpcomingIrrigationsComponent,
    RequestCallbackComponent,
    DkSelectPlantationComponent,
    MapWidgetComponent,
    WidgetInfoBoxComponent,
    SupportChatBubbleComponent,
  ],
  providers: [EnterpriseProxy, HomePageProxy, UserProxy],
  templateUrl: './home.component.html',
  styleUrl: './home.component.less',
})
export class HomeComponent implements OnInit {
  @ViewChild('requestCallback') requestCallbackEl: RequestCallbackComponent;
  userEnterprises: UserEnterpriseInfoViewModel[] = [];
  selectedEnterprise: UserEnterpriseInfoViewModel;
  loadingEnterprises = false;

  hasAnyLandsOrPlantations = false;
  isLandCheckCompleted = false;
  tutorialDialogVisible = false;

  hasAnyPremiumPlantation = false;
  hasIrrigationProgramAccess = false;
  selectedPlantationHasPremiumPlan = false;
  hasAnyPremiumForSpotDetection = false;
  hasAnyDevice = false;

  private readonly TUTORIAL_SHOWN_KEY = 'land_tutorial_dialog_shown';
  private readonly LAST_SELECTED_ENTERPRISE_ID_KEY = 'last_selected_enterprise_id_home';

  private homeWarningsAnalyticsData:
    | {
        weatherWarnings: number;
        pestsWarnings: number;
        diseaseRiskWarnings: number;
        hasPestDevice: boolean;
        fetchingError: boolean;
      }
    | undefined;

  private homeProblematicSpotsAnalyticsData:
    | {
        problematicSpotsCount: number;
        fetchingError: boolean;
      }
    | undefined;

  private lastTrackedEnterpriseId: string | null = null;
  private premiumEligibilityResolved = false;

  constructor(
    private _enterpriseProxy: EnterpriseProxy,
    private _localStorageService: LocalStorageService,
    private _tutorialService: TutorialService,
    private _router: Router,
    public plantationService: PlantationService,
    private translateService: TranslateService,
    private dialogService: DialogService,
    private mixpanelTracker: MixpanelTrackerService,
    private homeMapWidgetService: HomeMapWidgetService,
    private _userService: UserService,
    private _userProxy: UserProxy
  ) {}

  ngOnInit(): void {
    // Reset Home Map Engaged tracking so that each time
    // the user lands on Home, the first map interaction
    // for that visit can trigger the analytics event.
    this.homeMapWidgetService.resetMapEngagedTracking();
    this.applyPendingRegistrationProfile();
    this.getUserEnterprises();
  }

  /**
   * Profession was stored before the account existed. Write it, along with the
   * locale, timezone, and country captured in the same step, once home is shown.
   */
  private applyPendingRegistrationProfile(): void {
    const pending = this._userService.getPendingRegistrationProfile();
    if (!pending) {
      return;
    }

    const userId = this._localStorageService.getItem<string>('userId');
    if (!userId) {
      return;
    }

    const command = new UpdateUserProfileCommand({
      professionId: pending.professionId,
      name: undefined,
      surname: undefined,
      appId: undefined,
      customerId: undefined,
      countryId: pending.countryId,
      defaultEnterpriseId: undefined,
      localeId: pending.localeId,
      timeZoneId: pending.timeZoneId,
      units: undefined,
      notificationPreferences: undefined,
      engagementPreferences: undefined,
      roles: undefined,
      allCommunicationsAllowed: undefined,
      isMFAActive: undefined,
      forcePasswordChange: undefined,
      note: undefined,
    });

    this._userProxy.user_UpdateUserProfile(userId, command).subscribe({
      next: (response) => {
        if (response.isSuccess) {
          this._userService.clearPendingRegistrationProfile();
        }
      },
      error: () => {
        // Keep the pending profile so the next home visit can retry.
      },
    });
  }

  getUserEnterprises() {
    // Check if dialog was already shown before
    const wasDialogShown = this._localStorageService.getItem<boolean>(this.TUTORIAL_SHOWN_KEY);
    const lastSelectedEnterpriseId = this._localStorageService.getItem<string>(this.LAST_SELECTED_ENTERPRISE_ID_KEY);

    this.loadingEnterprises = true;
    this._enterpriseProxy
      .enterprise_GetUserEnterprises()
      .pipe(
        finalize(() => {
          this.isLandCheckCompleted = true;
          this.loadingEnterprises = false;
        })
      )
      .subscribe({
        next: (res: UserEnterpriseInfoViewModelListResponseResult) => {
          this.userEnterprises = res.result ?? [];
          if (this.userEnterprises?.length > 0) {
            // The stored enterprise can be gone by now (deleted, or access revoked).
            const lastSelectedEnterprise = lastSelectedEnterpriseId
              ? this.userEnterprises.find(e => e.enterpriseId === lastSelectedEnterpriseId)
              : undefined;
            this.onSelectEnterprise(lastSelectedEnterprise ?? this.userEnterprises[0], { trackAnalytics: false });

            let landCount = 0;
            this.userEnterprises.forEach((enterprise) => {
              landCount += enterprise.lands.length;
            });

            let plantationCount = 0;
            this.userEnterprises.forEach((enterprise) => {
              plantationCount += enterprise.lands.reduce((acc, land) => acc + (land.plantations.length ?? 0), 0);
            });
            if (wasDialogShown) {
              if (landCount < 1) {
                this._router.navigate(['/lands/add']);
                return;
              }
              if (plantationCount < 1) {
                this._router.navigate(['/lands']);
                return;
              }
            }
            if (!wasDialogShown) {
              if (landCount > 0) {
                this.tutorialDialogVisible = false;
                this.hasAnyLandsOrPlantations = true;
              } else {
                this.tutorialDialogVisible = true;
                this.hasAnyLandsOrPlantations = false;
              }
              return;
            }
          }
        },
        // The interceptor already toasts the failure; finalize() clears the loading state.
        error: () => {
          this.userEnterprises = [];
        },
      });
  }

  onSelectEnterprise(enterprise: UserEnterpriseInfoViewModel, options?: { trackAnalytics?: boolean }) {
    if (!enterprise) {
      return;
    }

    const prevEnterpriseId = this.selectedEnterprise?.enterpriseId ?? '';
    const nextEnterpriseId = enterprise?.enterpriseId ?? '';
    this.selectedEnterprise = enterprise;
    this._localStorageService.setItem(this.LAST_SELECTED_ENTERPRISE_ID_KEY, enterprise.enterpriseId);
    // Allow "Home Viewed" to fire again when enterprise changes (avoid duplicates when re-selecting same enterprise)
    if (prevEnterpriseId !== nextEnterpriseId) {
      this.lastTrackedEnterpriseId = null;
    }
    this.hasAnyLandsOrPlantations = false;
    this.hasAnyPremiumPlantation = false;
    this.hasIrrigationProgramAccess = false;
    this.hasAnyPremiumForSpotDetection = false;
    this.hasAnyDevice = false;
    this.homeWarningsAnalyticsData = undefined;
    this.homeProblematicSpotsAnalyticsData = undefined;
    this.premiumEligibilityResolved = false;
    this.checkLand();

    if (options?.trackAnalytics !== false) {
      this.mixpanelTracker.trackHomeEnterpriseSelected({
        'enterprise id': enterprise.enterpriseId ?? '',
        'enterprise name': enterprise.name ?? '',
      });
    }
  }

  checkLand() {
    this.isLandCheckCompleted = false;
    if (this.selectedEnterprise) {
      this.isLandCheckCompleted = true;
      this.plantationService.selectedPlantation$
      .subscribe((plantation) => {
        this.premiumEligibilityResolved = true;
        if (plantation) {
          this.selectedPlantationHasPremiumPlan = plantation.isPremium === true;
          this.hasAnyPremiumPlantation = this.plantationService.checkThisEnterpriseHasAnyPremiumPlantation(
            this.selectedEnterprise.enterpriseId
          );
          this.hasIrrigationProgramAccess =
            this.hasAnyPremiumPlantation ||
            this.plantationService.checkThisEnterpriseHasAnyIrrigationProgramAccess(
              this.selectedEnterprise.enterpriseId
            );
          this.hasAnyPremiumForSpotDetection = this.plantationService.checkThisEnterpriseHasAnyPremiumPlantation(
            this.selectedEnterprise.enterpriseId,
            true
          );
          this.hasAnyDevice = this.plantationService.checkThisEnterpriseHasAnyDevice(
            this.selectedEnterprise.enterpriseId
          );
        } else {
          this.selectedPlantationHasPremiumPlan = false;
          this.hasIrrigationProgramAccess = false;
        }
      });
      return;
    }
  }

  openLandsPopover() {
    this.tutorialDialogVisible = false;

    // Don't mark dialog as shown here - only mark when user actually clicks the "Add Land" button
    // this._localStorageService.setItem(this.TUTORIAL_SHOWN_KEY, true);

    // Trigger navigation bar lands popover through service
    this._tutorialService.showLandsPopover();
  }

  getPremium(selectedfeatureKey: PremiumFeatureKey) {
    this.dialogService.open(PurchasePremiumComponent, {
      modal: true,
      closable: true,
      header: this.translateService.instant('doktar_app.services.crop_monitoring.psd_premium_title'),
      inputValues: {
        selectedfeatureKey,
      },
    });
  }

  isMapFullScreen = false;
  fullScreenToggle() {
    this.isMapFullScreen = !this.isMapFullScreen;
  }

  openRequestCallback() {
    this.requestCallbackEl.showRequestCallback();
  }

  onHomeWarningsAnalyticsLoaded(event: {
    weatherWarnings: number;
    pestsWarnings: number;
    diseaseRiskWarnings: number;
    hasPestDevice: boolean;
    fetchingError: boolean;
  }) {
    this.homeWarningsAnalyticsData = event;
    this.tryTrackHomeViewed();
  }

  onHomeProblematicSpotsAnalyticsLoaded(event: { problematicSpotsCount: number; fetchingError: boolean }) {
    this.homeProblematicSpotsAnalyticsData = event;
    this.tryTrackHomeViewed();
  }

  private tryTrackHomeViewed(): void {
    const canFallbackToEmptySpots =
      this.premiumEligibilityResolved && this.hasAnyPremiumForSpotDetection === false;

    if (
      !this.selectedEnterprise ||
      !this.homeWarningsAnalyticsData ||
      (!this.homeProblematicSpotsAnalyticsData && !canFallbackToEmptySpots)
    ) {
      return;
    }

    const enterpriseId = this.selectedEnterprise.enterpriseId ?? '';

    if (this.lastTrackedEnterpriseId === enterpriseId) {
      return;
    }

    const warnings = this.homeWarningsAnalyticsData;
    const spots = this.homeProblematicSpotsAnalyticsData ?? {
      problematicSpotsCount: 0,
      fetchingError: false,
    };

    const errorCard: string[] = [];
    if (warnings.fetchingError) {
      errorCard.push('Warnings and Alerts');
    }
    if (spots.fetchingError) {
      errorCard.push('Problematic Spots');
    }

    const errorPage = warnings.fetchingError || spots.fetchingError;

    this.mixpanelTracker.trackHomeViewed({
      'error - page': errorPage,
      'error - card': errorCard,
      'number of weather warning': warnings.weatherWarnings,
      'number of pests warning': warnings.pestsWarnings,
      'number of disease risk warning': warnings.diseaseRiskWarnings,
      'number of all warning':
        warnings.weatherWarnings + warnings.pestsWarnings + warnings.diseaseRiskWarnings,
      'number of problematic spots': spots.problematicSpotsCount,
      'has premium plan': this.hasAnyPremiumForSpotDetection,
    });

    this.lastTrackedEnterpriseId = enterpriseId;
  }
}
